import { hasValue, isNullish } from "@core/foundation/types";
import { z } from "@core/zod";

import { BarcodeProduct } from "@apis/calorie-grid/models";

const OFF_PRODUCT_URL = "https://world.openfoodfacts.org/api/v2/product";

const OFF_STATUS_FOUND = 1;

const USER_AGENT =
  "CalorieGrid/0.1 (https://github.com/aptblocks-com/aptblocks)";

const HUNDRED_GRAMS = 100;

const ProductSchema = z.object(
  {
    brands: z.string().optional(),
    nutriments: z
      .object(
        {
          "energy-kcal_100g": z.number().optional(),
          "energy-kcal_serving": z.number().optional(),
        },
        {
          "energy-kcal_100g": "The product's energy per 100 g, in kcal",
          "energy-kcal_serving": "The product's energy per serving, in kcal",
        },
      )
      .optional(),
    product_name: z.string().optional(),
    product_quantity: z.coerce.number().optional(),
    serving_quantity: z.coerce.number().optional(),
  },
  {
    brands: "The product's brand names as Open Food Facts lists them",
    nutriments: "The product's nutrition facts",
    product_name: "The product's name",
    product_quantity: "The product's net quantity, read as its weight in grams",
    serving_quantity: "The quantity of one serving, in grams",
  },
);

type Product = z.infer<typeof ProductSchema>;

const ResponseSchema = z.object(
  { product: ProductSchema.optional(), status: z.number() },
  {
    product: "The product Open Food Facts holds for the barcode",
    status: "The lookup's status, 1 when the product was found",
  },
);

class OpenFoodFactsClient {
  public async findByBarcode(barcode: string): Promise<BarcodeProduct | null> {
    const response = await fetch(`${OFF_PRODUCT_URL}/${barcode}.json`, {
      headers: { "User-Agent": USER_AGENT },
    });

    const json = await response.json();

    const { product, status } = ResponseSchema.parse(json);

    if (status !== OFF_STATUS_FOUND || isNullish(product)) {
      return null;
    }

    const weight = this.resolveWeight(product);

    const totalCalories = this.resolveTotalCalories(product, weight);

    return BarcodeProduct.create({
      barcode,
      brand: product.brands,
      name: product.product_name,
      raw: JSON.stringify(json),
      source: "open-food-facts",
      totalCalories,
      weightGrams: weight,
    });
  }

  private resolveTotalCalories(
    product: Product,
    weight: null | number,
  ): null | number {
    if (isNullish(weight)) {
      return null;
    }

    const per100g = product.nutriments?.["energy-kcal_100g"];

    if (hasValue(per100g) && per100g > 0) {
      return Math.round(per100g * (weight / HUNDRED_GRAMS));
    }

    const perServing = product.nutriments?.["energy-kcal_serving"];

    if (
      hasValue(perServing) &&
      hasValue(product.serving_quantity) &&
      perServing > 0 &&
      product.serving_quantity > 0
    ) {
      return Math.round(perServing * (weight / product.serving_quantity));
    }

    return null;
  }

  private resolveWeight(product: Product): null | number {
    if (hasValue(product.product_quantity) && product.product_quantity > 0) {
      return product.product_quantity;
    }

    return null;
  }
}

export const openFoodFactsClient = new OpenFoodFactsClient();
